import { FC, useMemo, useState } from 'react';

import { formatPrice } from '@shared/utils/formatPrice';

import { rateOf } from './adminBugoStat.util';
import { OrderStatKind } from './adminOrderStat.types';
import {
  PenetrationBase,
  PenetrationBrandRow,
  PenetrationResult,
} from './adminPenetration.types';
import { denomOf, metricsOf } from './adminPenetration.util';
import {
  baselineIndexTone,
  formatBaselineIndex,
  TD_CLASS,
  TH_CLASS,
} from './AnalyticsShared';

//* 상조별 "모수 상가 몇 곳 중 몇 곳이 샀나". 분모(공유/발인)는 화면에서 고르고
//* 표는 그 선택을 그대로 따른다 — 요약 카드와 표의 %가 어긋나면 안 되기 때문.
//* 침투율은 숫자만으론 비교가 안 돼 셀 안에 막대를 같이 그린다.

type SortKey =
  | 'denomCount'
  | 'denomBuyingCount'
  | 'penetration'
  | 'baselineIndex'
  | 'feventCount'
  | 'orderCount'
  | 'amount'
  | 'amountPerFevent'
  | 'amountPerBuying'
  | 'avgOrderAmount'
  | 'ordersPerBuying';

interface Column {
  key: SortKey;
  label: string;
  title: string;
}

const columnsOf = (base: PenetrationBase): Column[] => {
  const denomLabel = base === 'shared' ? '공유상가' : '발인상가';
  return [
    {
      key: 'denomCount',
      label: denomLabel,
      title:
        base === 'shared'
          ? '부고를 1회 이상 공유한 상가 수 (분모)'
          : '기간 내 발인한 상가 수 (분모)',
    },
    {
      key: 'denomBuyingCount',
      label: '구매상가',
      title: `${denomLabel} 중 주문이 1건 이상 붙은 상가 수`,
    },
    { key: 'penetration', label: '구매율', title: `구매상가 ÷ ${denomLabel}` },
    {
      key: 'baselineIndex',
      label: '평균대비',
      title:
        '전체 구매율을 100%로 봤을 때 이 상조의 지수 — 80% 미만 빨강, 110% 초과 초록',
    },
    {
      key: 'feventCount',
      label: '발인상가',
      title: '기간 내 발인한 상가 수 (공유 여부와 무관한 전체)',
    },
    { key: 'orderCount', label: '주문', title: '유효 주문 건수' },
    { key: 'amount', label: '금액', title: '화환=결제액, 답례품=결제액 − 취소액' },
    {
      key: 'avgOrderAmount',
      label: '구매단가',
      title: '금액 ÷ 주문 — 주문 한 건의 평균 결제액',
    },
    {
      key: 'amountPerBuying',
      label: '구매상가당',
      title: '금액 ÷ 구매상가 — 산 상가 한 곳이 쓴 금액',
    },
    {
      key: 'ordersPerBuying',
      label: '상가당건수',
      title: '주문 ÷ 구매상가 — 산 상가 한 곳에서 몇 건이 나오나',
    },
    {
      key: 'amountPerFevent',
      label: `${denomLabel}당`,
      title: `금액 ÷ ${denomLabel} — 안 산 곳까지 포함, 상가 1곳이 얼마를 만드는가`,
    },
  ];
};

const penetrationOf = (row: PenetrationBrandRow, base: PenetrationBase) => {
  const { total, buying } = denomOf(row, base);
  return rateOf(buying, total);
};

//* 금액·건수도 분모와 같은 모수에서 낸다 — '공유상가' 분모일 땐 공유 상가 주문만
const amountPerFeventOf = (row: PenetrationBrandRow, base: PenetrationBase) => {
  const { total } = denomOf(row, base);
  return total > 0 ? metricsOf(row, base).amount / total : -1;
};
const amountPerBuyingOf = (row: PenetrationBrandRow, base: PenetrationBase) => {
  const { buying } = denomOf(row, base);
  return buying > 0 ? metricsOf(row, base).amount / buying : -1;
};
const ordersPerBuyingOf = (row: PenetrationBrandRow, base: PenetrationBase) => {
  const { buying } = denomOf(row, base);
  return buying > 0 ? metricsOf(row, base).orderCount / buying : -1;
};
const avgOrderAmountOf = (row: PenetrationBrandRow, base: PenetrationBase) => {
  const { orderCount, amount } = metricsOf(row, base);
  return orderCount > 0 ? amount / orderCount : -1;
};

/** 전체 구매율을 100%로 본 지수. 기준선이 0이면 낼 수 없다 */
const baselineIndexOf = (
  row: PenetrationBrandRow,
  base: PenetrationBase,
  baseline: number | null,
): number | null => {
  if (!baseline) return null;
  const rate = penetrationOf(row, base);
  return rate === null ? null : rate / 100 / baseline;
};

const sortValueOf = (
  row: PenetrationBrandRow,
  key: SortKey,
  base: PenetrationBase,
  baseline: number | null,
): number => {
  switch (key) {
    case 'denomCount':
      return denomOf(row, base).total;
    case 'denomBuyingCount':
      return denomOf(row, base).buying;
    case 'penetration':
      return penetrationOf(row, base) ?? -1;
    case 'baselineIndex':
      return baselineIndexOf(row, base, baseline) ?? -1;
    case 'amountPerFevent':
      return amountPerFeventOf(row, base);
    case 'amountPerBuying':
      return amountPerBuyingOf(row, base);
    case 'ordersPerBuying':
      return ordersPerBuyingOf(row, base);
    case 'avgOrderAmount':
      return avgOrderAmountOf(row, base);
    case 'orderCount':
      return metricsOf(row, base).orderCount;
    case 'amount':
      return metricsOf(row, base).amount;
    default:
      //* feventCount(전체 발인상가)는 분모 선택과 무관하게 늘 전체다
      return row[key];
  }
};

/** 비율 셀 — 막대 + 숫자 */
const GaugeCell: FC<{ rate: number | null }> = ({ rate }) => {
  if (rate === null) return <span className="text-gray-400">-</span>;
  return (
    <span className="flex items-center justify-end gap-1.5">
      <span className="h-1.5 w-10 shrink-0 overflow-hidden rounded-full bg-gray-200">
        <span
          className="block h-full rounded-full bg-indigo-500"
          style={{ width: `${Math.min(100, rate)}%` }}
        />
      </span>
      <span className="w-12 shrink-0 text-right font-semibold text-gray-900">
        {rate.toFixed(1)}%
      </span>
    </span>
  );
};

const cellOf = (
  row: PenetrationBrandRow,
  key: SortKey,
  base: PenetrationBase,
  baseline: number | null,
) => {
  switch (key) {
    case 'penetration':
      return <GaugeCell rate={penetrationOf(row, base)} />;
    case 'baselineIndex': {
      const index = baselineIndexOf(row, base, baseline);
      return (
        <span className={`font-semibold ${baselineIndexTone(index)}`}>
          {formatBaselineIndex(index)}
        </span>
      );
    }
    case 'amount':
      return formatPrice(Math.round(metricsOf(row, base).amount));
    case 'amountPerFevent':
    case 'amountPerBuying':
    case 'avgOrderAmount': {
      const value = sortValueOf(row, key, base, baseline);
      return value < 0 ? '-' : formatPrice(Math.round(value));
    }
    case 'ordersPerBuying': {
      const value = ordersPerBuyingOf(row, base);
      return value < 0 ? '-' : value.toFixed(1);
    }
    case 'denomCount':
    case 'denomBuyingCount':
    case 'orderCount':
      return sortValueOf(row, key, base, baseline).toLocaleString();
    default:
      return row[key].toLocaleString();
  }
};

export const AdminPenetrationTable: FC<{
  kind: OrderStatKind;
  base: PenetrationBase;
  result: PenetrationResult;
  onExport: () => void;
  onBrandClick: (brandKey: string, brandName: string) => void;
}> = function AdminPenetrationTable({ kind, base, result, onExport, onBrandClick }) {
  const [sortKey, setSortKey] = useState<SortKey>('denomCount');
  const [desc, setDesc] = useState(true);

  const columns = useMemo(() => columnsOf(base), [base]);

  const totalsRow: PenetrationBrandRow = useMemo(
    () => ({ ...result.totals, key: '', brandName: '' }),
    [result.totals],
  );

  //* 기준선 = 이 화면 전체의 구매율. 상조별 지수는 전부 여기에 견준다
  const baseline = useMemo(() => {
    const { total, buying } = denomOf(totalsRow, base);
    return total > 0 ? buying / total : null;
  }, [totalsRow, base]);

  const rows = useMemo(() => {
    const sorted = [...result.brands];
    sorted.sort((a, b) => {
      const diff =
        sortValueOf(a, sortKey, base, baseline) - sortValueOf(b, sortKey, base, baseline);
      return desc ? -diff : diff;
    });
    return sorted;
  }, [result.brands, sortKey, desc, base, baseline]);

  const onHeaderClick = (key: SortKey) => {
    if (key === sortKey) setDesc((prev) => !prev);
    else {
      setSortKey(key);
      setDesc(true);
    }
  };

  const kindLabel = kind === 'gift' ? '답례품' : '화환';

  return (
    <div className="rounded-lg bg-white p-3 shadow-smd">
      <div className="flex items-center justify-between">
        <p className="text-16 font-bold text-gray-900">
          상조별 {kindLabel} 구매율{' '}
          <span className="font-normal text-gray-500">({rows.length}곳)</span>
          <span className="ml-2 text-12 font-normal text-gray-500">
            행을 누르면 상가 목록
          </span>
        </p>
        <button
          type="button"
          onClick={onExport}
          className="shrink-0 rounded-lg bg-gray-800 px-3 py-1.5 text-13 font-medium text-white hover:bg-gray-700"
        >
          엑셀
        </button>
      </div>

      <div className="mt-2 overflow-x-auto">
        <table className="w-full min-w-[1120px]">
          <thead className="border-b border-gray-200">
            <tr>
              <th className={`${TH_CLASS} sticky left-0 bg-white text-left`} scope="col">
                상조
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`${TH_CLASS} cursor-pointer select-none hover:text-gray-900`}
                  onClick={() => onHeaderClick(column.key)}
                  title={column.title}
                >
                  {column.label}
                  {sortKey === column.key && (desc ? ' ▾' : ' ▴')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr
                key={row.key}
                className="cursor-pointer hover:bg-indigo-50"
                onClick={() => onBrandClick(row.key, row.brandName)}
                title={`${row.brandName} 상가 목록 보기`}
              >
                <td
                  className={`${TD_CLASS} sticky left-0 bg-white text-left font-semibold text-indigo-700 underline-offset-2 hover:underline`}
                >
                  {row.brandName}
                </td>
                {columns.map((column) => (
                  <td key={column.key} className={TD_CLASS}>
                    {cellOf(row, column.key, base, baseline)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-gray-200">
            <tr className="bg-gray-50 font-semibold">
              <td className={`${TD_CLASS} sticky left-0 bg-gray-50 text-left`}>합계</td>
              {columns.map((column) => (
                <td key={column.key} className={TD_CLASS}>
                  {cellOf(totalsRow, column.key, base, baseline)}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
